// react
import { Suspense } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetBasicEnergyAccountQuery } from '@/api/energy-analysis/basicEnergySlice';

// feature-components
import ServiceActivationSwitch from '@/features/service-manager/components/ServiceActivationSwitch';

export const Route = createFileRoute(
  '/$accountId/_admin/service-manager/$managedAccountId/basic-dashboards',
)({
  component: RouteComponent,
  staticData: { crumb: 'service-manager:services.basic-dashboards.title' },
});

function RouteComponent() {
  const { t } = useTranslation('service-management');
  const { managedAccountId } = useParams({
    from: '/$accountId/_admin/service-manager/$managedAccountId/basic-dashboards',
  });

  const { data: basicEnergySubscription } = useGetBasicEnergyAccountQuery({
    account_id: managedAccountId,
  });

  const basicDashboardServices: Array<{
    title: string;
    servicePath:
      | 'dashboard-power-basic'
      | 'dashboard-water-basic'
      | 'dashboard-heat-basic';
    basicEnergyType: 'Power' | 'Water' | 'Heat';
    isIncluded: boolean;
  }> = [
    {
      title: t('basicPower.title', 'Power'),
      servicePath: 'dashboard-power-basic',
      basicEnergyType: 'Power',
      isIncluded: !!basicEnergySubscription?.Power,
    },
    {
      title: t('basicWater.title', 'Water'),
      servicePath: 'dashboard-water-basic',
      basicEnergyType: 'Water',
      isIncluded: !!basicEnergySubscription?.Water,
    },
    {
      title: t('basicHeat.title', 'Heat'),
      servicePath: 'dashboard-heat-basic',
      basicEnergyType: 'Heat',
      isIncluded: !!basicEnergySubscription?.Heat,
    },
  ];

  return (
    <Stack spacing={2}>
      <Suspense>
        <Card>
          <CardHeader
            title={t('basicEnergy.title')}
            slotProps={{
              title: { variant: 'h6', fontWeight: 600 }
            }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('basicEnergy.description')}
              </Typography>

              {basicDashboardServices.map((service) => (
                <Stack
                  key={service.basicEnergyType}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {service.title}
                    </Typography>
                    <Chip
                      size="small"
                      color={service.isIncluded ? 'success' : 'default'}
                      label={
                        service.isIncluded
                          ? t('basicEnergy.statusChips.operational')
                          : t('basicEnergy.statusChips.inactive')
                      }
                    />
                  </Stack>

                  <ServiceActivationSwitch
                    managedAccountId={managedAccountId}
                    servicePath={service.servicePath}
                    ariaLabel={service.title}
                    basicEnergyType={service.basicEnergyType}
                  />
                </Stack>
              ))}
            </Stack>
          </CardContent>
          <Divider />
        </Card>
      </Suspense>
    </Stack>
  );
}
